<div class="store-page" [class.faded]="showFilterModal() || showGenerateCardModal() || showFundCardModal() || showStoreProfileModal()">
  <!-- Hero Section -->
  <section class="store-hero-section">
    <div class="hero-background-image"></div>
    <div class="hero-overlay"></div>
    <div class="hero-content">
      <button type="button" class="back-button-hero" (click)="goBack()">
        <i class="pi pi-arrow-left"></i>
        <span>Back to Marketplace</span>
      </button>
      <div class="store-branding">
        @if (platformLogo()) {
          <img [src]="platformLogo()!" [alt]="platformName() + ' logo'" class="store-hero-logo" />
        }
        <h1 class="store-hero-title">{{ platformName() }}</h1>
      </div>
      <div class="hero-actions">
        <button type="button" class="hero-action-btn" (click)="openStoreProfileModal()">
          <i class="pi pi-info-circle"></i>
          <span>Store Info</span>
        </button>
        <button type="button" class="hero-action-btn" (click)="goToWishlist()">
          <i class="pi pi-heart"></i>
          <span>Wishlist</span>
        </button>
        @if (authService.isAuthenticated() && authService.getUser()) {
          <div class="shopping-card-hero">
            @if (shoppingCardLoading()) {
              <div class="card-loading">
                <p-progressSpinner [style]="{ width: '20px', height: '20px' }"></p-progressSpinner>
              </div>
            } @else if (shoppingCardExists() && shoppingCard()) {
              <div class="virtual-card-compact">
                <div class="card-chip-small"></div>
                <div class="card-info-compact">
                  <span class="card-number-compact">{{ getShoppingCardNumber() }}</span>
                  <span class="card-balance-compact">{{ getCardBalance() }}</span>
                </div>
              </div>
            } @else {
              <button type="button" class="generate-card-btn-compact" (click)="generateShoppingCard()" [disabled]="shoppingCardLoading()">
                <i class="pi pi-credit-card"></i>
                <span>Get Card</span>
              </button>
            }
          </div>
        }
      </div>
    </div>
  </section>

  <!-- Quick Access Cards Section -->
  <section class="quick-access-section">
    <div class="section-container">
      <h2 class="section-heading">Quick Access</h2>
      <div class="quick-access-grid">
        <div class="quick-access-card" (click)="openStoreProfileModal()">
          <div class="card-icon-wrapper">
            <i class="pi pi-eye"></i>
          </div>
          <div class="card-content">
            <h3 class="card-title">Store Profile</h3>
            <p class="card-description">View store information</p>
          </div>
          <i class="pi pi-arrow-right card-arrow"></i>
        </div>

        <div class="quick-access-card" (click)="goToOrders()">
          <div class="card-icon-wrapper">
            <i class="pi pi-shopping-bag"></i>
          </div>
          <div class="card-content">
            <h3 class="card-title">Orders</h3>
            <p class="card-description">View your order history</p>
          </div>
          <i class="pi pi-arrow-right card-arrow"></i>
        </div>

        @if (authService.isAuthenticated() && supportsSavedCards()) {
          <div class="quick-access-card" (click)="goToCards()">
            <div class="card-icon-wrapper">
              <i class="pi pi-credit-card"></i>
            </div>
            <div class="card-content">
              <h3 class="card-title">Saved Cards</h3>
              <p class="card-description">Manage your saved payment cards</p>
            </div>
            <i class="pi pi-arrow-right card-arrow"></i>
          </div>
        }

        @if (isBillGatewayConfigured()) {
          <div class="quick-access-card" (click)="goToBillsTransactions()">
            <div class="card-icon-wrapper">
              <i class="pi pi-receipt"></i>
            </div>
            <div class="card-content">
              <h3 class="card-title">Bills Transactions</h3>
              <p class="card-description">View bills payment history</p>
            </div>
            <i class="pi pi-arrow-right card-arrow"></i>
          </div>
        }
      </div>
    </div>
  </section>

  <!-- Filters Section -->
  <section class="filters-section">
    <div class="filters-container">
      <div class="search-wrapper">
        <div class="search-box">
          <i class="pi pi-search search-icon"></i>
          <input 
            type="text" 
            [(ngModel)]="searchQuery"
            placeholder="Search products..."
            class="search-input"
            (keyup.enter)="onSearch()"
          />
          @if (searchQuery()) {
            <button 
              type="button" 
              class="clear-search"
              (click)="searchQuery.set('')"
            >
              <i class="pi pi-times"></i>
            </button>
          }
        </div>
        <div class="action-buttons">
          <button type="button" class="action-btn" (click)="openFilterModal()" title="Filter">
            <i class="pi pi-filter"></i>
            <span>Filter</span>
          </button>
          @if (isBillGatewayConfigured()) {
            <button type="button" class="action-btn" title="Bills Payment" (click)="goToBillsPayment()">
              <i class="pi pi-money-bill"></i>
              <span>Bills Payment</span>
            </button>
          }
          <button type="button" class="action-btn" title="Fund Shopping Card" (click)="onFundCardClick()">
            <i class="pi pi-wallet"></i>
            <span>Fund Card</span>
          </button>
        </div>
      </div>

      <div class="category-wrapper">
        <div class="category-selector">
          <button 
            type="button"
            class="category-btn"
            [class.active]="selectedCategoryId() === null"
            (click)="onCategoryChange(null)"
          >
            <i class="pi pi-th-large category-icon"></i>
            <span>All Categories</span>
          </button>
          @for (category of categories(); track category.id) {
            <button 
              type="button"
              class="category-btn"
              [class.active]="selectedCategoryId() === category.id"
              (click)="onCategoryChange(category.id)"
            >
              <i [class]="getCategoryIcon(category) + ' category-icon'"></i>
              <span>{{ category.name }}</span>
            </button>
          }
        </div>
      </div>
    </div>
  </section>

  <!-- Main Content -->
  <main class="store-container">
    <!-- Loading State -->
    @if (loading()) {
      <div class="store-preloader">
        <div class="phone-preloader">
          <div class="phone-frame">
            <div class="phone-screen">
              <div class="phone-notch"></div>
              <div class="rolling-ball-container">
                <div class="rolling-ball"></div>
              </div>
              <div class="loading-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        </div>
        <p class="preloader-text">Loading products...</p>
      </div>
    }

    <!-- Error State -->
    @if (error() && !loading()) {
      <div class="error-container">
        <div class="error-icon">⚠️</div>
        <p class="error-message">{{ error() }}</p>
        <p-button label="Retry" (onClick)="loadStoreData()" [raised]="true" severity="primary"></p-button>
      </div>
    }

    <!-- Products Grid -->
    @if (!loading() && !error() && filteredProducts().length > 0) {
      <section class="products-section">
        <h2 class="section-heading">
          {{ getSelectedCategoryName() }}
        </h2>
        <div class="products-grid">
          @for (product of filteredProducts(); track product.id) {
            <div class="product-card" (click)="onProductClick(product)">
              <div class="product-image-container">
                <img 
                  [src]="getProductImage(product)" 
                  [alt]="product.name"
                  class="product-image"
                  (error)="$any($event.target).src='https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=400&h=400&fit=crop'"
                />
                @if (product.discountPercentage && product.discountPercentage > 0) {
                  <div class="discount-badge">
                    -{{ product.discountPercentage }}%
                  </div>
                }
                <button 
                  type="button"
                  class="wishlist-button"
                  [class.active]="isInWishlist(product.id)"
                  (click)="toggleWishlist(product, $event)"
                  [title]="isInWishlist(product.id) ? 'Remove from wishlist' : 'Add to wishlist'"
                >
                  <i [class]="isInWishlist(product.id) ? 'pi pi-heart-fill' : 'pi pi-heart'"></i>
                </button>
              </div>
              <div class="product-info">
                <h3 class="product-name">{{ product.name }}</h3>
                @if (product.brand) {
                  <div class="product-brand">
                    @if (getBrandLogo(product.brand)) {
                      <img 
                        [src]="getBrandLogo(product.brand)!" 
                        [alt]="product.brand.name"
                        class="brand-logo"
                        (error)="$any($event.target).style.display='none'"
                      />
                    }
                    <span class="brand-name">{{ product.brand.name }}</span>
                  </div>
                }
                <div class="product-price">
                  <span class="current-price">{{ formatPrice(product.price) }}</span>
                  @if (product.compareAtPrice && product.compareAtPrice > product.price) {
                    <span class="old-price">{{ formatPrice(product.compareAtPrice) }}</span>
                  }
                </div>
                @if (product.averageRating && product.averageRating > 0) {
                  <div class="product-rating">
                    <span class="rating-stars">⭐</span>
                    <span class="rating-value">{{ product.averageRating.toFixed(1) }}</span>
                    <span class="rating-count">({{ product.totalReviews || 0 }})</span>
                  </div>
                }
                @if (product.stockQuantity <= 0) {
                  <p class="out-of-stock">Out of Stock</p>
                } @else {
                  <button 
                    type="button"
                    class="add-to-cart-button"
                    (click)="addToCart(product, $event)"
                    title="Add to cart"
                  >
                    <i class="pi pi-shopping-cart"></i>
                    <span>Add to Cart</span>
                  </button>
                }
              </div>
            </div>
          }
        </div>
      </section>
    }

    <!-- Empty State -->
    @if (!loading() && !error() && filteredProducts().length === 0) {
      <div class="empty-container">
        <div class="empty-icon">📦</div>
        <h2 class="empty-message">No products found</h2>
        <p class="empty-submessage">Try adjusting your filters or search terms</p>
      </div>
    }
  </main>
</div>

<!-- Filter Modal -->
<p-dialog 
  [visible]="showFilterModal()"
  (visibleChange)="showFilterModal.set($event)"
  [modal]="true" 
  [style]="{ width: '90vw', maxWidth: '600px' }"
  [closable]="true"
  [draggable]="false"
  [resizable]="false"
  styleClass="filter-modal"
  (onHide)="closeFilterModal()"
>
  <ng-template pTemplate="header">
    <h3 class="modal-title">Filter Products</h3>
  </ng-template>
  
  <div class="filter-modal-content">
    <!-- Categories Section -->
    <div class="filter-section">
      <h4 class="filter-section-title">Categories</h4>
      <div class="filter-list">
        @for (category of categories(); track category.id) {
          <div 
            class="filter-item" 
            [class.selected]="isCategorySelected(category.id)"
            (click)="toggleCategory(category.id)"
          >
            <i [class]="getCategoryIcon(category)"></i>
            <span>{{ category.name }}</span>
            @if (isCategorySelected(category.id)) {
              <i class="pi pi-check check-icon"></i>
            }
          </div>
        }
      </div>
    </div>

    <!-- Brands Section -->
    <div class="filter-section">
      <h4 class="filter-section-title">Brands</h4>
      <div class="filter-list">
        @for (brand of brands(); track brand.id) {
          <div 
            class="filter-item" 
            [class.selected]="isBrandSelected(brand.id)"
            (click)="toggleBrand(brand.id)"
          >
            @if (getBrandLogo(brand)) {
              <img [src]="getBrandLogo(brand)!" [alt]="brand.name" class="brand-logo-small" />
            } @else {
              <i class="pi pi-tag"></i>
            }
            <span>{{ brand.name }}</span>
            @if (isBrandSelected(brand.id)) {
              <i class="pi pi-check check-icon"></i>
            }
          </div>
        }
      </div>
    </div>
  </div>

  <ng-template pTemplate="footer">
    <button type="button" pButton label="Filter" (click)="applyFilters()" class="p-button-primary"></button>
  </ng-template>
</p-dialog>

<!-- Filtering Preloader -->
@if (filtering()) {
  <div class="filtering-overlay">
    <div class="filtering-content">
      <div class="filtering-spinner">
        <p-progressSpinner 
          [style]="{ width: '60px', height: '60px' }"
          styleClass="purple-spinner"
        ></p-progressSpinner>
      </div>
      <p class="filtering-text">Filtering products...</p>
    </div>
  </div>
}

<p-toast position="bottom-right"></p-toast>

<!-- Generate Card Modal -->
<p-dialog 
  [visible]="showGenerateCardModal()"
  (visibleChange)="showGenerateCardModal.set($event)"
  [modal]="true"
  [style]="{ width: '90vw', maxWidth: '500px' }"
  [closable]="true"
  [draggable]="false"
  [resizable]="false"
  styleClass="generate-card-modal"
  (onHide)="closeGenerateCardModal()"
>
  <ng-template pTemplate="header">
    <h3 class="modal-title">Generate Shopping Card</h3>
  </ng-template>
  
  <div class="modal-content">
    <p>You don't have a shopping card for this store. Would you like to generate one?</p>
    <p class="modal-subtitle">Once generated, you'll be able to fund your card and start shopping.</p>
  </div>

  <ng-template pTemplate="footer">
    <div class="modal-footer">
      <button 
        type="button" 
        pButton 
        label="Generate Card" 
        (click)="generateShoppingCard()" 
        [disabled]="shoppingCardLoading()"
        [loading]="shoppingCardLoading()"
        class="p-button-primary"
      ></button>
    </div>
  </ng-template>
</p-dialog>

<!-- Fund Card Modal -->
<p-dialog 
  [visible]="showFundCardModal()"
  (visibleChange)="showFundCardModal.set($event)"
  [modal]="true"
  [style]="{ width: '90vw', maxWidth: '500px' }"
  [closable]="true"
  [draggable]="false"
  [resizable]="false"
  styleClass="fund-card-modal"
  (onHide)="closeFundCardModal()"
>
  <ng-template pTemplate="header">
    <h3 class="modal-title">Fund Shopping Card</h3>
  </ng-template>
  
  <div class="modal-content">
    <div class="form-group">
      <label for="fundingAmount">Amount (₦)</label>
      <p-inputNumber
        id="fundingAmount"
        [ngModel]="fundingAmount()"
        (ngModelChange)="fundingAmount.set($event ?? 0)"
        [showButtons]="true"
        [min]="100"
        [max]="10000000"
        [step]="100"
        currency="NGN"
        mode="decimal"
        [disabled]="fundingLoading()"
        styleClass="full-width"
        placeholder="Enter amount"
      ></p-inputNumber>
      <small class="form-hint">Minimum amount: ₦100</small>
    </div>
    
    @if (shoppingCard()) {
      <div class="current-balance">
        <span class="balance-label">Current Balance:</span>
        <span class="balance-value">{{ getCardBalance() }}</span>
      </div>
    }
  </div>

  <ng-template pTemplate="footer">
    <div class="modal-footer">
      <button 
        type="button" 
        pButton 
        label="Fund Card" 
        (click)="fundShoppingCard()" 
        [disabled]="fundingLoading() || !fundingAmount() || fundingAmount() < 100"
        [loading]="fundingLoading()"
        class="p-button-primary"
      ></button>
    </div>
  </ng-template>
</p-dialog>

<!-- Store Profile Modal -->
<p-dialog 
  [visible]="showStoreProfileModal()"
  (visibleChange)="showStoreProfileModal.set($event)"
  [modal]="true" 
  [style]="{ width: '90vw', maxWidth: '600px' }"
  [closable]="true"
  [draggable]="false"
  [resizable]="false"
  styleClass="store-profile-modal"
  (onHide)="closeStoreProfileModal()"
>
  <ng-template pTemplate="header">
    <h2 class="modal-title">Store Profile</h2>
  </ng-template>
  
  <div class="store-profile-content">
    @if (platformProfile()) {
      <div class="profile-section">
        @if (platformProfile()!.businessEmail) {
          <div class="profile-item">
            <label class="profile-label">Email:</label>
            <p class="profile-value">{{ platformProfile()!.businessEmail }}</p>
          </div>
        }
        
        @if (platformProfile()!.businessPhoneNumber) {
          <div class="profile-item">
            <label class="profile-label">Phone:</label>
            <p class="profile-value">{{ platformProfile()!.businessPhoneNumber }}</p>
          </div>
        }
        
        @if (platformProfile()!.businessAddress) {
          <div class="profile-item">
            <label class="profile-label">Address:</label>
            <p class="profile-value">{{ platformProfile()!.businessAddress }}</p>
          </div>
        }
      </div>
    } @else {
      <div class="loading-profile">
        <p-progressSpinner [style]="{ width: '40px', height: '40px' }"></p-progressSpinner>
        <p>Loading store profile...</p>
      </div>
    }
  </div>
  
  <ng-template pTemplate="footer">
    <p-button 
      label="Close" 
      (click)="closeStoreProfileModal()" 
      [text]="true"
      severity="secondary"
    ></p-button>
  </ng-template>
</p-dialog>
